import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import Layout from '../../../components/layout/Layout';
import { Bell, Plus, Check } from 'lucide-react';
import { billingApi } from '../../../api/billingApi';
import type { Notification, NotificationFormData } from '../types/notification';
import NotificationForm from '../components/notifications/NotificationForm';
import NotificationsList from '../components/notifications/NotificationsList';
import { useError } from '../../../context/ErrorContext';
import { useConfirmDialog } from '../../../components/common/ConfirmDialog';

const NotificationsPage: React.FC = () => {
  const { confirm, dialog } = useConfirmDialog();
  const { currentUser } = useAuth();
  const { handleFirebaseError, clearErrors } = useError();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [businesses, setBusinesses] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingNotification, setEditingNotification] = useState<Notification | null>(null);
  const [saving, setSaving] = useState(false);
  const [] = useState(false);

  const fetchNotifications = async () => {
    if (!currentUser?.uid) return;

    try {
      setLoading(true);
      clearErrors(); // Limpiar errores previos

      // Fechas ya vienen como Date desde el adaptador.
      const notificationsList = await billingApi.adminListNotifications();

      setNotifications(notificationsList.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()));
    } catch (error) {
      console.error('Error al obtener notificaciones:', error);
      handleFirebaseError(error, 'notifications-fetch');
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinesses = async () => {
    if (!currentUser?.uid) return;

    try {
      clearErrors(); // Limpiar errores previos
      // Lista de negocios para el selector de target 'specific' (derivada de las
      // suscripciones; businessId = slug del tenant).
      const rows = await billingApi.adminListSubscriptions();
      // target 'specific' usa el ID NUMÉRICO del tenant (uid), no el slug: la entrega
      // (lib/notifications.listForTenant) compara target_value contra el id numérico.
      const businessesList = rows
        .filter(r => r.uid)
        .map(r => ({ id: r.uid as string, name: r.businessName || 'Negocio sin nombre' }));

      setBusinesses(businessesList);
    } catch (error) {
      console.error('Error al obtener negocios:', error);
      handleFirebaseError(error, 'businesses-fetch');
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchBusinesses();
  }, [currentUser?.uid]);

  const handleSubmit = async (formData: NotificationFormData) => {
    if (!currentUser?.uid) return;

    try {
      setSaving(true);
      clearErrors(); // Limpiar errores previos

      if (editingNotification) {
        await billingApi.updateNotification(editingNotification.id, formData);
        setSuccess('Notificación actualizada correctamente');
      } else {
        await billingApi.createNotification(formData);
        setSuccess('Notificación creada correctamente');
      }

      setShowForm(false);
      setEditingNotification(null);
      fetchNotifications();

      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (error) {
      console.error('Error al guardar notificación:', error);
      handleFirebaseError(error, 'notification-save');
    } finally {
      setSaving(false);
    }
  };


  const handleEdit = (notification: Notification) => {
    clearErrors(); // Limpiar errores previos
    setEditingNotification(notification);
    setShowForm(true);
  };

  const handleDelete = async (notificationId: string) => {
    const ok = await confirm({
      title: '¿Eliminar esta notificación?',
      message: 'Desaparece de la lista y no se puede recuperar.',
      confirmLabel: 'Eliminar',
    });
    if (!ok) return;

    try {
      clearErrors(); // Limpiar errores previos
      await billingApi.deleteNotification(notificationId);
      setSuccess('Notificación eliminada correctamente');
      fetchNotifications();

      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (error) {
      console.error('Error al eliminar notificación:', error);
      handleFirebaseError(error, 'notification-delete');
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center bg-surface p-4 rounded-lg shadow-sm animate-fade-in-up" style={{animationDelay: '0.1s'}}>
          <div>
            <h1 className="text-2xl font-bold text-content flex items-center">
              <Bell className="h-6 w-6 mr-2 text-accent" />
              Gestión de Notificaciones
            </h1>
            <p className="mt-1 text-sm text-content-2">
              Administra los avisos para los negocios
            </p>
          </div>
          {!showForm && (
            <button
              onClick={() => {
                clearErrors(); // Limpiar errores al abrir el formulario
                setShowForm(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-solid hover:bg-accent-solid/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-colors animate-fade-in-up"
              style={{animationDelay: '0.15s'}}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nueva Notificación
            </button>
          )}
        </div>

        {success && (
          <div className="bg-green-50 dark:bg-green-500/10 border-l-4 border-green-400 p-4 rounded-r-md shadow-sm animate-fade-in-up" style={{animationDelay: '0.15s'}}>
            <div className="flex">
              <div className="flex-shrink-0">
                <Check className="h-5 w-5 text-green-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700 dark:text-green-300">{success}</p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{animationDelay: '0.15s'}}>
          <div className="px-4 py-5 sm:p-6">
            {showForm ? (
              <div className="bg-surface p-6 rounded-lg shadow-sm animate-fade-in-up" style={{animationDelay: '0.1s'}}>
                <h2 className="text-lg font-medium text-content mb-4 flex items-center">
                  <Bell className="h-5 w-5 mr-2 text-accent" />
                  {editingNotification ? 'Editar Notificación' : 'Nueva Notificación'}
                </h2>
                <NotificationForm
                  initialData={editingNotification || undefined}
                  onSubmit={handleSubmit}
                  onCancel={() => {
                    setShowForm(false);
                    setEditingNotification(null);
                    clearErrors(); // Limpiar errores al cancelar
                  }}
                  businesses={businesses}
                  loading={saving}
                />
              </div>
            ) : (
              <NotificationsList
                notifications={notifications}
                onEdit={handleEdit}
                onDelete={handleDelete}
                loading={loading}
              />
            )}
          </div>
        </div>
      </div>
      {dialog}
    </Layout>
  );
};

export default NotificationsPage;